/**
 * The map is a route of its own (MapLibre is ~275 kB gzipped plus its CSS), so nothing on the
 * home screen loads it. It is fetched when someone shows interest — hovering, focusing or
 * touching the preview, or the map button — and, on the desktop layout, once the page has
 * settled and the browser is idle (unless the connection is slow or data-saving). Opening the
 * map then costs no wait.
 *
 * On a phone the same chunk is fetched when the radar card nears the viewport (`preloadMapWhenNear`) or
 * the moment a finger lands on it or on a map button, together with what the opening picture is drawn
 * from (the picture's code, the border/city data and, in map/warm.ts, its tiles), so a tap shows the
 * place's radar at once even on a first visit.
 */
import { warmStaticRadar } from '../map/MapIntro'

export const loadMapPage = () => {
  // The opening picture's pieces, in parallel with the larger MapLibre chunk.
  void warmStaticRadar()
  void Promise.all([import('./geo/borders.json'), import('./geo/cities.json')]).catch(() => undefined)
  // Start requests in parallel with the larger MapLibre chunk.
  void import('../map/warm').then(m => m.warmCurrentMap()).catch(() => undefined)
  return import('../map/MapPage')
}

let started = false

/** Starts fetching the map code (idempotent). */
export function preloadMap(): void {
  if (started) return
  started = true
  void loadMapPage().catch(() => {
    started = false // offline: a later hover tries again
  })
}

interface SaveDataConnection {
  saveData?: boolean
  effectiveType?: string
}

/** False on data-saver and 2G/3G-class connections, where an unrequested 275 kB isn't free. */
export function canPrefetch(): boolean {
  const c = (navigator as Navigator & { connection?: SaveDataConnection }).connection
  if (!c) return true
  return !c.saveData && !/^(slow-2g|2g|3g)$/.test(c.effectiveType ?? '')
}

/**
 * Phones and tablets: fetch the map's code once the radar card is within `margin` px of the viewport
 * (fires once, at an idle moment, unless the connection is slow or data-saving). The desktop layout
 * has its own idle prefetch and hover warm-up. Returns the cleanup.
 */
export function preloadMapWhenNear(el: Element | null, margin = 200): () => void {
  if (!el || typeof IntersectionObserver === 'undefined') return () => {}
  if (window.matchMedia?.('(min-width: 900px)').matches) return () => {}
  const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void }
  let idle = 0
  const go = () => {
    if (canPrefetch() && document.visibilityState === 'visible') preloadMap()
  }
  const io = new IntersectionObserver(
    (entries) => {
      if (!entries.some((e) => e.isIntersecting)) return
      io.disconnect() // once
      idle = w.requestIdleCallback ? w.requestIdleCallback(go, { timeout: 2000 }) : window.setTimeout(go, 0)
    },
    { rootMargin: `${margin}px` },
  )
  io.observe(el)
  return () => {
    io.disconnect()
    if (idle) {
      if (w.cancelIdleCallback) w.cancelIdleCallback(idle)
      else window.clearTimeout(idle)
    }
  }
}

/** Idle prefetch (desktop layout only) `delayMs` after the page settled (the first complete forecast). Returns cleanup. */
export function preloadMapWhenSettled(delayMs = 6000): () => void {
  let timer = 0
  let idle = 0
  const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void }
  const go = () => {
    // Phones and tablets (the single-column layout) only start on a touch of the preview or the
    // map button: an unrequested 275 kB isn't free on a cellular plan.
    if (window.matchMedia && !window.matchMedia('(min-width: 900px)').matches) return
    if (!canPrefetch() || document.visibilityState !== 'visible') return
    if (w.requestIdleCallback) idle = w.requestIdleCallback(preloadMap, { timeout: 4000 })
    else preloadMap()
  }
  const intent = (event: Event) => {
    if ((event.target as Element)?.closest?.('.map-preview, [data-nav-key^="map-"]')) preloadMap()
  }
  document.addEventListener('pointerdown', intent, { passive: true })
  document.addEventListener('touchstart', intent, { passive: true })
  const settle = () => {
    if (!timer) timer = window.setTimeout(go, delayMs)
  }
  window.addEventListener('wf:report-full', settle, { once: true })
  // Demo / offline builds never fire the report mark: fall back to the load event.
  if (document.readyState === 'complete') settle()
  else window.addEventListener('load', settle, { once: true })
  return () => {
    document.removeEventListener('pointerdown', intent)
    document.removeEventListener('touchstart', intent)
    window.removeEventListener('wf:report-full', settle)
    window.removeEventListener('load', settle)
    window.clearTimeout(timer)
    if (idle && w.cancelIdleCallback) w.cancelIdleCallback(idle)
  }
}
